import { Platform } from 'react-native';
import * as Font from 'expo-font';

const WEB_FONTS: { family: string; url: string; weight: string; format: string }[] = [
  // Panton fonts (English) — TTF
  { family: 'Panton-Regular',  url: '/assets/fonts/Panton-Trial-Regular.ttf',  weight: '400', format: 'truetype' },
  { family: 'Panton-Medium',   url: '/assets/fonts/Panton-Trial-SemiBold.ttf', weight: '500', format: 'truetype' },
  { family: 'Panton-SemiBold', url: '/assets/fonts/Panton-Trial-SemiBold.ttf', weight: '600', format: 'truetype' },
  { family: 'Panton-Bold',     url: '/assets/fonts/Panton-Trial-Bold.ttf',     weight: '700', format: 'truetype' },
  { family: 'Panton-Black',    url: '/assets/fonts/Panton-Trial-Black.ttf',    weight: '900', format: 'truetype' },
  // RH-Zak Arabic fonts — OTF
  // RH-Thin registered at weight 400 so ::placeholder font-family override wins without fighting font-weight
  { family: 'RH-Thin',     url: '/assets/fonts/RH-Zak%20Thin.otf',  weight: '400', format: 'opentype' },
  { family: 'RH-Regular',  url: '/assets/fonts/RH-Zak%20Reg.otf',   weight: '400', format: 'opentype' },
  { family: 'RH-Medium',   url: '/assets/fonts/RH-Zak%20Bold.otf',  weight: '500', format: 'opentype' },
  { family: 'RH-SemiBold', url: '/assets/fonts/RH-Zak%20Bold.otf',  weight: '600', format: 'opentype' },
  { family: 'RH-Bold',     url: '/assets/fonts/RH-Zak%20Bold.otf',  weight: '700', format: 'opentype' },
  { family: 'RH-Black',    url: '/assets/fonts/RH-Zak%20Bold.otf',  weight: '900', format: 'opentype' },
  // Tajawal — supports both Latin and Arabic glyphs natively, TTF. No true
  // SemiBold/Thin weights exist in this family; Thin aliases to ExtraLight
  // and SemiBold aliases to Medium (same precedent as the RH set reusing
  // Bold for both SemiBold and Bold).
  { family: 'Tajawal-Thin',    url: '/assets/fonts/Tajawal-ExtraLight.ttf', weight: '400', format: 'truetype' },
  { family: 'Tajawal-Light',   url: '/assets/fonts/Tajawal-Light.ttf',   weight: '300', format: 'truetype' },
  { family: 'Tajawal-Regular', url: '/assets/fonts/Tajawal-Regular.ttf', weight: '400', format: 'truetype' },
  { family: 'Tajawal-Medium',  url: '/assets/fonts/Tajawal-Medium.ttf',  weight: '500', format: 'truetype' },
  { family: 'Tajawal-SemiBold', url: '/assets/fonts/Tajawal-Medium.ttf', weight: '600', format: 'truetype' },
  { family: 'Tajawal-Bold',    url: '/assets/fonts/Tajawal-Bold.ttf',    weight: '700', format: 'truetype' },
  { family: 'Tajawal-ExtraBold', url: '/assets/fonts/Tajawal-ExtraBold.ttf', weight: '800', format: 'truetype' },
  { family: 'Tajawal-Black',   url: '/assets/fonts/Tajawal-Black.ttf',   weight: '900', format: 'truetype' },
];

// Inject global CSS for placeholder styling on web
const injectPlaceholderStyles = (): void => {
  const styleId = 'sendifier-placeholder-styles';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    input::placeholder, textarea::placeholder {
      color: #9CA3AF !important;
      font-size: 0.875em !important;
      opacity: 1 !important;
    }
    [dir="rtl"] input::placeholder, [dir="rtl"] textarea::placeholder {
      font-family: Tajawal-Light, sans-serif !important;
      font-weight: 400 !important;
    }
    [dir="ltr"] input::placeholder, [dir="ltr"] textarea::placeholder {
      font-family: Tajawal-Regular, sans-serif !important;
      font-weight: 400 !important;
    }
    /* Disable ligatures for Panton-Trial — trial font missing fi, fl, ff ligature glyphs */
    * {
      font-variant-ligatures: no-common-ligatures !important;
      font-feature-settings: "liga" 0, "clig" 0 !important;
    }
  `;
  document.head.appendChild(style);
};

// Web font loading via FontFace API — forces actual download and parse.
// Uses Promise.allSettled (not Promise.all) so one bad/missing font file
// doesn't reject the whole batch and silently leave every family
// unregistered — each entry loads/fails independently.
const loadWebFonts = async (): Promise<void> => {
  const results = await Promise.allSettled(
    WEB_FONTS.map(async ({ family, url, weight, format }) => {
      const isPanton = family.startsWith('Panton');
      const options: FontFaceDescriptors = { weight };
      // Restrict Panton to Latin letters and digits only — trial font lacks many symbols
      // Any character outside this range (*, #, !, @, etc.) falls back to system font
      if (isPanton) {
        options.unicodeRange = 'U+0041-005A, U+0061-007A, U+0030-0039, U+0020, U+002E, U+002C, U+003A, U+003B, U+0027, U+0022, U+00C0-00FF';
      }
      const font = new FontFace(family, `url(${url}) format('${format}')`, options);
      await font.load();
      (document.fonts as any).add(font);
    })
  );
  results.forEach((result, i) => {
    if (result.status === 'rejected') {
      console.error(`❌ [fonts] Failed to load web font "${WEB_FONTS[i].family}" from ${WEB_FONTS[i].url}:`, result.reason);
    }
  });
  injectPlaceholderStyles();
};

const NATIVE_FONTS: Record<string, number> = {
  'Panton-Regular':  require('../../assets/fonts/Panton-Trial-Regular.ttf'),
  'Panton-Medium':   require('../../assets/fonts/Panton-Trial-SemiBold.ttf'),
  'Panton-SemiBold': require('../../assets/fonts/Panton-Trial-SemiBold.ttf'),
  'Panton-Bold':     require('../../assets/fonts/Panton-Trial-Bold.ttf'),
  'Panton-Black':    require('../../assets/fonts/Panton-Trial-Black.ttf'),
  'RH-Thin':     require('../../assets/fonts/RH-Thin.otf'),
  'RH-Regular':  require('../../assets/fonts/RH-Regular.ttf'),
  'RH-Medium':   require('../../assets/fonts/RH-Medium.ttf'),
  'RH-SemiBold': require('../../assets/fonts/RH-SemiBold.ttf'),
  'RH-Bold':     require('../../assets/fonts/RH-Bold.ttf'),
  'RH-Black':    require('../../assets/fonts/RH-Black.ttf'),
  'Tajawal-Thin':      require('../../assets/fonts/Tajawal-ExtraLight.ttf'),
  'Tajawal-Light':     require('../../assets/fonts/Tajawal-Light.ttf'),
  'Tajawal-Regular':   require('../../assets/fonts/Tajawal-Regular.ttf'),
  'Tajawal-Medium':    require('../../assets/fonts/Tajawal-Medium.ttf'),
  'Tajawal-SemiBold':  require('../../assets/fonts/Tajawal-Medium.ttf'),
  'Tajawal-Bold':      require('../../assets/fonts/Tajawal-Bold.ttf'),
  'Tajawal-ExtraBold': require('../../assets/fonts/Tajawal-ExtraBold.ttf'),
  'Tajawal-Black':     require('../../assets/fonts/Tajawal-Black.ttf'),
};

export const loadFonts = async (): Promise<void> => {
  if (Platform.OS === 'web') {
    // Web fonts are loaded via inline CSS
    await loadWebFonts();
    return;
  }
  // Native — load each family independently (Promise.allSettled) so one
  // bad/missing font file doesn't reject the whole batch and leave every
  // family unregistered, same rationale as loadWebFonts above.
  const results = await Promise.allSettled(
    Object.entries(NATIVE_FONTS).map(([name, source]) => Font.loadAsync({ [name]: source }))
  );
  results.forEach((result, i) => {
    if (result.status === 'rejected') {
      const name = Object.keys(NATIVE_FONTS)[i];
      console.error(`❌ [fonts] Failed to load native font "${name}":`, result.reason);
    }
  });
};

// Check if a specific font is loaded
export const isFontLoaded = (fontFamily: string): boolean => {
  if (Platform.OS === 'web') {
    if (document.fonts && document.fonts.check) {
      return document.fonts.check(`12px "${fontFamily}"`);
    }
    return true; // Assume fonts are available on web
  }
  return true; // On native, assume fonts are loaded if no error
};

export const fonts = {
  panton: {
    regular: 'Panton-Regular',
    medium: 'Panton-Medium',
    semibold: 'Panton-SemiBold',
    bold: 'Panton-Bold',
    black: 'Panton-Black',
  },
  rh: {
    thin: 'RH-Thin',
    regular: 'RH-Regular',
    medium: 'RH-Medium',
    semibold: 'RH-SemiBold',
    bold: 'RH-Bold',
    black: 'RH-Black',
  },
  tajawal: {
    thin: 'Tajawal-Thin',
    regular: 'Tajawal-Regular',
    medium: 'Tajawal-Medium',
    semibold: 'Tajawal-SemiBold',
    bold: 'Tajawal-Bold',
    black: 'Tajawal-Black',
  },
};
